import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const formPageEnums = {
    HEADER: 'HEADER',
    FOOTER: 'FOOTER',
    FORM_SETTINGS: 'FORM_SETTINGS',
};

const MAX_HISTORY = 10;
const DEBOUNCE_MS = 600;

export const FormBuilderContext = createContext();

export const FormBuilderProvider = ({ children, isReportBuilder = false, initialFormList, initialImageUrls }) => {
    // Signed rich-text image URLs ride along inside the persisted template
    // (`template.imageUrls`), but they must live in the `imageUrls` context —
    // NOT in `formList` — so `resolveRichTextImages` can inject `<img src>` on
    // read-only renders (note detail, report preview) and so they don't pollute
    // the dirty check (see the `imageUrls` state comment below). Split on init
    // so detail/preview consumers that just pass the raw template still resolve.
    const { imageUrls: embeddedImageUrls, ...initialFormListRest } = initialFormList || {};
    const [formList, _setFormList] = useState(initialFormList ? initialFormListRest : {
        header: { content: '', properties: {}, show: false },
        footer: { content: '', properties: {}, show: false },
        sections: [
            { id: uuidv4(), name: 'Information', attributes: [] },
        ],
    });
    const [activeSection, _setActiveSection] = useState(1);
    const [activeFormPage, _setActiveFormPage] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [selectedGoalId, setSelectedGoalId] = useState(null);
    const [selectedTargetId, setSelectedTargetId] = useState(null);
    // An inline field inside a RICH_TEXT, picked for the Properties panel:
    // `{ attributeId, fieldId }`. Keyed by attribute id, so it only applies
    // while that attribute is the selected one.
    const [selectedInlineField, setSelectedInlineField] = useState(null);
    const [error, setError] = useState(null);
    const [onDragStart, setOnDragStart] = useState(false);
    const sectionRefs = useRef({});
    // Per-goal DOM refs (keyed `${sectionIndex}-${goalId}`) so the sections
    // panel can scroll a specific goal block into view in the report builder.
    const goalRefs = useRef({});
    const [isDirty, setIsDirty] = useState(false);
    // Previewing a historical version (read-only); blocks version capture while set.
    const [previewing, setPreviewing] = useState(null);
    const initialFormListStr = useRef();

    // Server-delivered fresh signed URLs for rich-text `<img data-key>`
    // references. Kept OUT of `formList` so it doesn't pollute the
    // dirty check (signed URLs change on every read — X-Amz-Date is
    // in the URL — but they represent no user-facing change to the
    // template). Threaded to RichTextElement via context / props.
    const [imageUrls, setImageUrls] = useState(initialImageUrls || embeddedImageUrls || {});

    // ── Undo / Redo history ──
    const pastRef = useRef([]);       // past snapshots (JSON strings)
    const futureRef = useRef([]);     // redo snapshots (JSON strings)
    const debounceTimer = useRef(null);
    const pendingSnapshot = useRef(null); // captures state before first change in a burst
    const isUndoRedo = useRef(false); // flag to skip snapshot during undo/redo
    const lastCommittedStr = useRef(null); // clean JSON of last committed state (immune to mutations)
    const [historyVersion, setHistoryVersion] = useState(0); // trigger re-renders for canUndo/canRedo

    const canUndo = pastRef.current.length > 0;
    const canRedo = futureRef.current.length > 0;

    // Keep lastCommittedStr in sync after every render
    useEffect(() => {
        if (formList != null) {
            lastCommittedStr.current = JSON.stringify(formList);
        }
    }, [formList]);

    // Push a snapshot to the past stack
    const pushSnapshot = useCallback((snapshot) => {
        const past = pastRef.current;
        // Don't push if identical to last snapshot
        if (past.length > 0 && past[past.length - 1] === snapshot) return;
        past.push(snapshot);
        if (past.length > MAX_HISTORY) {
            past.shift();
        }
        setHistoryVersion((v) => v + 1);
    }, []);

    // Detect structural changes (add/delete/reorder attributes or sections,
    // picked goals, and a goal's own field list in a goal section)
    const isStructuralChange = (prev, next) => {
        const sameIds = (prevAttrs = [], nextAttrs = []) =>
            prevAttrs.length === nextAttrs.length && prevAttrs.every((attr, j) => attr?.id === nextAttrs[j]?.id);
        const prevSections = prev?.sections || [];
        const nextSections = next?.sections || [];
        if (prevSections.length !== nextSections.length) return true;
        for (let i = 0; i < prevSections.length; i++) {
            if (!sameIds(prevSections[i]?.attributes, nextSections[i]?.attributes)) return true;
            // Picking or removing a goal adds / drops a whole goal block.
            if (!sameIds(prevSections[i]?.selectedGoals, nextSections[i]?.selectedGoals)) return true;
            const nextGoals = nextSections[i]?.goals || {};
            for (const goalId of Object.keys(nextGoals)) {
                if (!nextGoals[goalId]?.ownAttributes) continue;
                if (!sameIds(prevSections[i]?.goals?.[goalId]?.attributes, nextGoals[goalId]?.attributes)) return true;
            }
        }
        return false;
    };

    // Wrapped setFormList that also records history
    const setFormList = useCallback((updater) => {
        _setFormList((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            const nextStr = JSON.stringify(next);

            // Use lastCommittedStr as the true previous state (immune to caller mutations)
            const prevStr = lastCommittedStr.current;

            // Skip if no previous state yet or state hasn't changed
            if (!prevStr || prevStr === nextStr) {
                lastCommittedStr.current = nextStr;
                return next;
            }

            if (!isUndoRedo.current) {
                const prevParsed = JSON.parse(prevStr);
                const structural = isStructuralChange(prevParsed, next);

                if (structural) {
                    // Structural change: flush any pending value-change snapshot first
                    if (debounceTimer.current) {
                        clearTimeout(debounceTimer.current);
                        if (pendingSnapshot.current !== null) {
                            pushSnapshot(pendingSnapshot.current);
                        }
                        debounceTimer.current = null;
                    }
                    // Push current state immediately (no debounce)
                    pushSnapshot(prevStr);
                    pendingSnapshot.current = null;
                } else {
                    // Value change: debounce to group rapid keystrokes
                    if (!debounceTimer.current) {
                        pendingSnapshot.current = prevStr;
                    }
                    clearTimeout(debounceTimer.current);
                    debounceTimer.current = setTimeout(() => {
                        if (pendingSnapshot.current !== null) {
                            pushSnapshot(pendingSnapshot.current);
                            pendingSnapshot.current = null;
                        }
                        debounceTimer.current = null;
                    }, DEBOUNCE_MS);
                }
                // Any genuine user change clears the redo stack
                futureRef.current = [];
            }

            lastCommittedStr.current = nextStr;
            return next;
        });
    }, [pushSnapshot]);

    const undo = useCallback(() => {
        // Flush any pending debounced snapshot immediately
        clearTimeout(debounceTimer.current);
        if (pendingSnapshot.current !== null) {
            pushSnapshot(pendingSnapshot.current);
            pendingSnapshot.current = null;
        }
        debounceTimer.current = null;

        if (pastRef.current.length === 0) return;
        const past = pastRef.current;
        const future = futureRef.current;

        // Save current state to future
        _setFormList((current) => {
            future.push(JSON.stringify(current));

            // Pop last past state
            const previousSnapshot = past.pop();
            const previousState = JSON.parse(previousSnapshot);

            isUndoRedo.current = true;
            lastCommittedStr.current = previousSnapshot;
            setHistoryVersion((v) => v + 1);

            setTimeout(() => {
                isUndoRedo.current = false;
            }, 0);

            return previousState;
        });
    }, [pushSnapshot]);

    const redo = useCallback(() => {
        // Flush any pending debounced snapshot immediately
        clearTimeout(debounceTimer.current);
        if (pendingSnapshot.current !== null) {
            pushSnapshot(pendingSnapshot.current);
            pendingSnapshot.current = null;
        }
        debounceTimer.current = null;

        if (futureRef.current.length === 0) return;
        const past = pastRef.current;
        const future = futureRef.current;

        // Save current state to past
        _setFormList((current) => {
            past.push(JSON.stringify(current));
            if (past.length > MAX_HISTORY) {
                past.shift();
            }

            // Pop next future state
            const nextSnapshot = future.pop();
            const nextState = JSON.parse(nextSnapshot);

            isUndoRedo.current = true;
            lastCommittedStr.current = nextSnapshot;
            setHistoryVersion((v) => v + 1);

            setTimeout(() => {
                isUndoRedo.current = false;
            }, 0);

            return nextState;
        });
    }, [pushSnapshot]);

    // Reset history stacks (call after initialization to clear phantom entries)
    const resetHistory = useCallback(() => {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
        pendingSnapshot.current = null;
        pastRef.current = [];
        futureRef.current = [];
        setHistoryVersion((v) => v + 1);
    }, []);

    // Keyboard shortcuts: Ctrl/Cmd+Z for undo, Ctrl/Cmd+Shift+Z for redo
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isCtrlOrMeta = e.ctrlKey || e.metaKey;
            if (!isCtrlOrMeta || e.key.toLowerCase() !== 'z') return;

            // Don't intercept when user is inside a rich text editor
            const isInsideEditor = !!e.target?.closest('.tiptap-editor-wrapper, .tiptap-toolbar, .tiptap');
            if (isInsideEditor) return;

            e.preventDefault();
            if (e.shiftKey) {
                redo();
            } else {
                undo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo]);

    // Picking a section drops any goal / target picked inside it; callers that
    // select a goal set it right after, in the same batch.
    const setActiveSection = (val) => {
        _setActiveSection(val);
        _setActiveFormPage(null);
        setSelectedIndex(null);
        setSelectedGoalId(null);
        setSelectedTargetId(null);
    };

    const setActiveFormPage = (val) => {
        _setActiveFormPage(val);
        _setActiveSection(null);
        setSelectedIndex(null);
    };

    const value = {
        formList,
        setFormList,
        activeSection,
        setActiveSection,
        activeFormPage,
        setActiveFormPage,
        selectedIndex,
        setSelectedIndex,
        selectedGoalId,
        setSelectedGoalId,
        selectedTargetId,
        setSelectedTargetId,
        selectedInlineField,
        setSelectedInlineField,
        error,
        setError,
        onDragStart,
        goalRefs,
        setOnDragStart,
        sectionRefs,
        isReportBuilder,
        isDirty,
        setIsDirty,
        previewing,
        setPreviewing,
        initialFormListStr,
        imageUrls,
        setImageUrls,
        // Undo / Redo
        undo,
        redo,
        canUndo,
        canRedo,
        resetHistory,
        setFormListRaw: _setFormList,
    };

    return <FormBuilderContext.Provider value={value}>{children}</FormBuilderContext.Provider>;
};

export const useFormBuilder = () => {
    const context = useContext(FormBuilderContext);
    if (!context) {
        return {
            formList: null,
            setFormList: null,
            activeSection: null,
            setActiveSection: () => {},
            selectedIndex: null,
            setSelectedIndex: () => {},
            selectedGoalId: null,
            setSelectedGoalId: () => {},
            selectedTargetId: null,
            setSelectedTargetId: () => {},
            selectedInlineField: null,
            setSelectedInlineField: () => {},
            error: null,
            setError: () => {},
            onDragStart: false,
        goalRefs: { current: {} },
            setOnDragStart: () => {},
            sectionRefs: { current: {} },
            isReportBuilder: false,
            isDirty: false,
            setIsDirty: () => {},
            previewing: null,
            setPreviewing: () => {},
            initialFormListStr: { current: null },
            imageUrls: {},
            setImageUrls: () => {},
            undo: () => {},
            redo: () => {},
            canUndo: false,
            canRedo: false,
            resetHistory: () => {},
            setFormListRaw: () => {},
        };
    }
    return context;
};
